/**
 * AddDatasetPage provides functionality for displaying, adding, and editing datasets.
 * It serves as the main container for dataset management operations.
 */

import React, { useState, useEffect } from "react";
import Datagrid from "./Datagrid";
import InputForm from "./InputForm";
import EditForm from "./EditForm";
import { useAuth } from "hooks/useAuth";
import PyramidCard from "components/common/pyramid/PyramidCard";
import { horusApi } from "services/api/horus";

const AddDataset = () => {
  // Required services
  const api = horusApi();
  const { accessToken, user, userList, companyList } = useAuth();

  // State management for the component
  const [datasets, setDatasets] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDataset, setEditingDataset] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetches datasets from the API
   */
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await api.getDatasets({
        onlyReady: false,
        includeUserTransitiveGroups: true,
      });
      const datasets = Array.isArray(response)
        ? response
        : response.result || [];
      setDatasets(datasets);
    } catch (error) {
      console.error("Error fetching datasets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Effect to fetch datasets when the component mounts
   * or when the access token changes
   */
  useEffect(() => {
    fetchData();
  }, [accessToken]);

  /**
   * Handles clicking on a row in the datagrid
   * @param {Object} rowData - Data of the clicked row
   */
  const handleRowClick = (rowData) => {
    setEditingDataset(rowData);
  };

  /**
   * Handles cancellation of the edit operation
   */
  const handleEditCancel = () => {
    setEditingDataset(null);
  };

  /**
   * Handles confirmation of dataset edits
   * Updates the dataset in the backend and refreshes the data
   *
   * @param {Object} updatedData - The updated dataset information
   */
  const handleEditConfirm = async (updatedData) => {
    try {
      await api.updateDataset(updatedData);
      await fetchData();
      setEditingDataset(null);
    } catch (error) {
      console.error("Error updating dataset:", error);
    }
  };

  const handleDownload = (e) => {
    const link = document.createElement("a");
    link.href = "/help/Horus-Input.pdf";
    link.download = "Horus-Input.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ position: "relative" }}>
      <Datagrid
        userList={userList}
        companyList={companyList}
        datasets={datasets}
        isLoading={isLoading}
        onAddButtonClick={() => setIsModalOpen(true)}
        onRefresh={fetchData}
        onDownload={handleDownload}
        onRowClick={handleRowClick}
      />

      {isModalOpen && (
        <InputForm
          isOpen={isModalOpen}
          onSuccess={() => {
            setIsModalOpen(false);
            fetchData();
          }}
        />
      )}

      <EditForm
        isVisible={Boolean(editingDataset)}
        onCancel={handleEditCancel}
        onConfirm={handleEditConfirm}
        dataset={editingDataset}
      />
    </div>
  );
};

const AddDatasetPage = () => {
  return (
    <PyramidCard title="Dataset">
      <AddDataset />
    </PyramidCard>
  );
};

export default AddDatasetPage;
